/**
 * Debug utilities for testing Phase 5 viewport navigation
 * Exposed to window for browser console testing
 */

import { useAppStore } from '@store/appStore'
import { calculateVisibleFramesFromViewport } from '@utils/visibleFrames'
import { swipeToWorldDirection, findCamerasInDirection, scoreCandidates } from '@utils/gestureConversion'

// Get fresh state
const getState = () => useAppStore.getState()

/**
 * Test 1: Basic Pan Right
 * Pan viewport 500px to the right and log visible frames
 */
export function testPanRight() {
  console.log('\n🧪 Test 1: Pan Right 500px')
  console.log('━'.repeat(60))
  
  const state = getState()
  const startPos = { ...state.viewportWorldPosition }
  console.log(`Starting position: (${startPos.x}, ${startPos.y})`)
  
  // Pan right 500px
  state.setViewportPosition(startPos.x + 500, startPos.y)
  
  const newPos = getState().viewportWorldPosition
  console.log(`New position: (${newPos.x}, ${newPos.y})`)
  console.log(`Delta: (${newPos.x - startPos.x}, ${newPos.y - startPos.y})`)
  
  // Calculate visible frames
  const visible = calculateVisibleFramesFromViewport(
    getState().viewportWorldPosition,
    getState().viewportSize,
    getState().frameWorldPositions,
    { width: 450, height: 800 } // frame size
  )
  
  console.log(`\nVisible frames: ${visible.length}`)
  visible.slice(0, 5).forEach(f => {
    console.log(`  Frame ${f.frameIndex}: ${(f.coverage * 100).toFixed(1)}% coverage, offset (${f.offset.x.toFixed(0)}, ${f.offset.y.toFixed(0)})`)
  })
  
  if (visible.length > 0) {
    const winner = visible[0]
    console.log(`\n🏆 Winner: Frame ${winner.frameIndex} (${winner.coverage.toFixed(1)}% coverage)`)
  }
}

/**
 * Test 2: Basic Pan Left
 * Pan viewport 300px to the left and log visible frames
 */
export function testPanLeft() {
  console.log('\n🧪 Test 2: Pan Left 300px')
  console.log('━'.repeat(60))
  
  const state = getState()
  const startPos = { ...state.viewportWorldPosition }
  console.log(`Starting position: (${startPos.x}, ${startPos.y})`)
  
  // Pan left 300px
  state.setViewportPosition(startPos.x - 300, startPos.y)
  
  const newPos = getState().viewportWorldPosition
  console.log(`New position: (${newPos.x}, ${newPos.y})`)
  console.log(`Delta: (${newPos.x - startPos.x}, ${newPos.y - startPos.y})`)
  
  // Calculate visible frames
  const visible = calculateVisibleFramesFromViewport(
    getState().viewportWorldPosition,
    getState().viewportSize,
    getState().frameWorldPositions,
    { width: 450, height: 800 }
  )
  
  console.log(`\nVisible frames: ${visible.length}`)
  visible.slice(0, 5).forEach(f => {
    console.log(`  Frame ${f.frameIndex}: ${(f.coverage * 100).toFixed(1)}% coverage, offset (${f.offset.x.toFixed(0)}, ${f.offset.y.toFixed(0)})`)
  })
  
  if (visible.length > 0) {
    const winner = visible[0]
    console.log(`\n🏆 Winner: Frame ${winner.frameIndex} (${(winner.coverage * 100).toFixed(1)}% coverage)`)
  }
}

/**
 * Test 3: Jump to Frame
 * Center viewport on a specific frame
 */
export function testJumpToFrame(frameIndex: number) {
  console.log(`\n🧪 Test 3: Jump to Frame ${frameIndex}`)
  console.log('━'.repeat(60))
  
  const state = getState()
  state.centerViewportOnFrame(frameIndex)
  
  const newPos = getState().viewportWorldPosition
  console.log(`Viewport centered at: (${newPos.x}, ${newPos.y})`)
  
  // Calculate visible frames
  const visible = calculateVisibleFramesFromViewport(
    getState().viewportWorldPosition,
    getState().viewportSize,
    getState().frameWorldPositions,
    { width: 450, height: 800 }
  )
  
  console.log(`\nVisible frames: ${visible.length}`)
  visible.slice(0, 5).forEach(f => {
    console.log(`  Frame ${f.frameIndex}: ${(f.coverage * 100).toFixed(1)}% coverage`)
  })
  
  const targetVisible = visible.find(f => f.frameIndex === frameIndex)
  if (targetVisible) {
    console.log(`\n✅ Target frame ${frameIndex} is visible (${targetVisible.coverage.toFixed(1)}% coverage)`)
  } else {
    console.log(`\n⚠️ Target frame ${frameIndex} is NOT visible`)
  }
}

/**
 * Test 4: Viewport Info
 * Display current viewport state
 */
export function testViewportInfo() {
  console.log('\n🧪 Test 4: Viewport Info')
  console.log('━'.repeat(60))
  
  const state = getState()
  console.log('Viewport State:')
  console.log(`  Position: (${state.viewportWorldPosition.x}, ${state.viewportWorldPosition.y})`)
  console.log(`  Size: ${state.viewportSize.width}×${state.viewportSize.height}`)
  console.log(`  Version: ${state.viewportVersion}`)
  
  console.log('\nWorld Space Info:')
  const positions = state.frameWorldPositions
  if (positions.length > 0) {
    const xCoords = positions.map(p => p.worldX)
    const minX = Math.min(...xCoords)
    const maxX = Math.max(...xCoords)
    console.log(`  Total frames: ${positions.length}`)
    console.log(`  X range: ${minX.toFixed(0)} to ${maxX.toFixed(0)} (width: ${(maxX - minX).toFixed(0)}px)`)
    
    console.log('\n📊 Frame Positions (first 10):')
    positions.slice(0, 10).forEach(p => {
      const marker = p.frameIndex === state.currentCameraIndex ? '🏆' : '  '
      console.log(`${marker} Frame ${p.frameIndex}: (${p.worldX.toFixed(1)}, ${p.worldY.toFixed(1)})`)
    })
    if (positions.length > 10) {
      console.log(`   ... (${positions.length - 10} more frames)`)
    }
  } else {
    console.log('  No frames loaded')
  }
  
  console.log('\nCurrent Frame:')
  console.log(`  Index: ${state.currentCameraIndex}`)
}

/**
 * Test 5: Smooth Pan Simulation
 * Simulate smooth panning with multiple steps
 */
export function testSmoothPan(steps: number = 10, totalDelta: number = 500) {
  console.log(`\n🧪 Test 5: Smooth Pan Simulation (${steps} steps, ${totalDelta}px)`)
  console.log('━'.repeat(60))
  
  const state = getState()
  const startPos = state.viewportWorldPosition.x
  const stepSize = totalDelta / steps
  
  console.log(`Starting at x=${startPos.toFixed(0)}`)
  console.log(`Step size: ${stepSize.toFixed(1)}px`)
  
  for (let i = 1; i <= steps; i++) {
    const newX = startPos + (stepSize * i)
    state.setViewportPosition(newX, state.viewportWorldPosition.y)
    
    const currentX = getState().viewportWorldPosition.x
    const expectedX = startPos + (stepSize * i)
    const error = Math.abs(currentX - expectedX)
    
    if (i % 2 === 0 || i === steps) { // Log every other step
      console.log(`  Step ${i}: x=${currentX.toFixed(0)} (expected=${expectedX.toFixed(0)}, error=${error.toFixed(2)}px)`)
    }
  }
  
  const finalPos = getState().viewportWorldPosition.x
  const totalError = Math.abs(finalPos - (startPos + totalDelta))
  
  console.log(`\n✅ Final position: x=${finalPos.toFixed(0)}`)
  console.log(`   Expected: x=${(startPos + totalDelta).toFixed(0)}`)
  console.log(`   Error: ${totalError.toFixed(2)}px`)
  
  if (totalError < 1) {
    console.log('   ✅ PASS: No error accumulation')
  } else {
    console.log('   ⚠️ WARNING: Error accumulation detected')
  }
}

/**
 * Test 6: Reset to Origin
 * Reset viewport to (0, 0)
 */
export function testReset() {
  console.log('\n🧪 Test 6: Reset to Origin')
  console.log('━'.repeat(60))
  
  const state = getState()
  state.setViewportPosition(0, 0)
  
  const newPos = getState().viewportWorldPosition
  console.log(`Viewport reset to: (${newPos.x}, ${newPos.y})`)
}

/**
 * Test 7: Show All Frames
 * Center viewport to see all frames at once
 */
export function testShowAll() {
  console.log('\n🧪 Test 7: Show All Frames')
  console.log('━'.repeat(60))
  
  const state = getState()
  const positions = state.frameWorldPositions
  
  if (positions.length === 0) {
    console.log('❌ No frames loaded')
    return
  }
  
  // Calculate world space bounds
  const xCoords = positions.map(p => p.worldX)
  const yCoords = positions.map(p => p.worldY)
  const minX = Math.min(...xCoords)
  const maxX = Math.max(...xCoords)
  const minY = Math.min(...yCoords)
  const maxY = Math.max(...yCoords)
  
  // Calculate center of all frames
  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2
  
  console.log(`World space bounds:`)
  console.log(`  X: ${minX.toFixed(0)} to ${maxX.toFixed(0)} (width: ${(maxX - minX).toFixed(0)}px)`)
  console.log(`  Y: ${minY.toFixed(0)} to ${maxY.toFixed(0)} (height: ${(maxY - minY).toFixed(0)}px)`)
  console.log(`\nCentering viewport at: (${centerX.toFixed(0)}, ${centerY.toFixed(0)})`)
  
  // Center viewport on world space center
  state.setViewportPosition(centerX, centerY)
  
  console.log(`\n✅ Viewport centered - press 'D' to see collage view!`)
  console.log(`   You should see all ${positions.length} frames in the view`)
}

/**
 * TEST 8: swipeToWorldDirection()
 * Test Phase 2.3 swipe-to-direction conversion
 */
export function testSwipeToWorldDirection() {
  console.log('\n🧪 Test 8: swipeToWorldDirection()')
  console.log('━'.repeat(60))
  
  const state = getState()
  const currentCam = state.cameras[state.currentCameraIndex]
  
  if (!currentCam) {
    console.log('❌ No current camera')
    return
  }
  
  console.log(`Current camera: ${currentCam.id} at (${currentCam.position[0].toFixed(2)}, ${currentCam.position[1].toFixed(2)}, ${currentCam.position[2].toFixed(2)})`)
  
  // Test 4 directions
  const tests = [
    { name: 'RIGHT', x: 100, y: 0 },
    { name: 'LEFT', x: -100, y: 0 },
    { name: 'DOWN', x: 0, y: 100 },
    { name: 'UP', x: 0, y: -100 },
  ]
  
  tests.forEach(test => {
    const dir = swipeToWorldDirection(test.x, test.y, currentCam.rotation)
    const mag = Math.sqrt(dir[0]*dir[0] + dir[1]*dir[1] + dir[2]*dir[2])
    console.log(`  Swipe ${test.name.padEnd(6)}: [${dir[0].toFixed(3)}, ${dir[1].toFixed(3)}, ${dir[2].toFixed(3)}] (magnitude: ${mag.toFixed(3)})`)
  })
  
  console.log('✅ All swipes converted successfully')
}

/**
 * TEST 9: findCamerasInDirection()
 * Test cone-based camera search
 */
export function testFindCamerasInDirection() {
  console.log('\n🧪 Test 9: findCamerasInDirection()')
  console.log('━'.repeat(60))
  
  const state = getState()
  const currentCam = state.cameras[state.currentCameraIndex]
  
  if (!currentCam) {
    console.log('❌ No current camera')
    return
  }
  
  // Test direction: right
  const dir = swipeToWorldDirection(100, 0, currentCam.rotation)
  console.log(`Searching in direction: [${dir[0].toFixed(3)}, ${dir[1].toFixed(3)}, ${dir[2].toFixed(3)}]`)
  
  const candidates = findCamerasInDirection(
    currentCam.position,
    dir,
    state.cameras,
    45, // cone angle
    2.0 // max distance
  )
  
  console.log(`\nFound ${candidates.length} candidates in 45° cone, max distance 2.0m:`)
  candidates.slice(0, 5).forEach((cam, i) => {
    const dist = Math.sqrt(
      Math.pow(cam.position[0] - currentCam.position[0], 2) +
      Math.pow(cam.position[1] - currentCam.position[1], 2) +
      Math.pow(cam.position[2] - currentCam.position[2], 2)
    )
    console.log(`  ${i+1}. Camera ${cam.id} - distance: ${dist.toFixed(3)}m`)
  })
  
  console.log('✅ Cone search completed')
}

/**
 * TEST 10: scoreCandidates()
 * Test dual-metric scoring (60% spatial + 40% continuity)
 */
export function testScoreCandidates() {
  console.log('\n🧪 Test 10: scoreCandidates()')
  console.log('━'.repeat(60))
  
  const state = getState()
  const currentCamId = state.currentCameraIndex
  const currentCam = state.cameras[currentCamId]
  
  if (!currentCam || !state.pointCloudMappings) {
    console.log('❌ Missing camera or point cloud mappings')
    return
  }
  
  // Get candidates in a direction
  const dir = swipeToWorldDirection(100, 0, currentCam.rotation)
  const candidates = findCamerasInDirection(
    currentCam.position,
    dir,
    state.cameras,
    45,
    2.0
  )
  
  if (candidates.length === 0) {
    console.log('❌ No candidates found in cone')
    return
  }
  
  // Score them
  const scored = scoreCandidates(currentCamId, candidates, state.pointCloudMappings)
  
  console.log(`Current camera: ${currentCamId}`)
  console.log(`\nScored ${scored.length} candidates:`)
  scored.slice(0, 5).forEach((item, i) => {
    console.log(`  ${i+1}. Camera ${item.camera.id}: score = ${item.score.toFixed(4)}`)
  })
  
  if (scored.length > 0) {
    console.log(`\n🏆 Best candidate: Camera ${scored[0].camera.id} (score: ${scored[0].score.toFixed(4)})`)
  }
  
  console.log('✅ Scoring completed')
}

/**
 * TEST HELPER: Run all Phase 2.3 tests
 */
export function testAllGestureConversion() {
  console.log('\n' + '█'.repeat(60))
  console.log('█  PHASE 2.3 GESTURE CONVERSION TEST SUITE')
  console.log('█'.repeat(60))
  
  testSwipeToWorldDirection()
  testFindCamerasInDirection()
  testScoreCandidates()
  
  console.log('\n' + '█'.repeat(60))
  console.log('█  ALL TESTS COMPLETED')
  console.log('█'.repeat(60))
}

/**
 * Expose store for manual console testing
 */
export function getStoreForDebug() {
  return getState()
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).testPanRight = testPanRight;
  (window as any).testPanLeft = testPanLeft;
  (window as any).testJumpToFrame = testJumpToFrame;
  (window as any).testViewportInfo = testViewportInfo;
  (window as any).testSmoothPan = testSmoothPan;
  (window as any).testReset = testReset;
  (window as any).testShowAll = testShowAll;
  (window as any).testSwipeToWorldDirection = testSwipeToWorldDirection;
  (window as any).testFindCamerasInDirection = testFindCamerasInDirection;
  (window as any).testScoreCandidates = testScoreCandidates;
  (window as any).testAllGestureConversion = testAllGestureConversion;
  (window as any).getStoreForDebug = getStoreForDebug;
  
  console.log('🧪 Debug utilities loaded. Available commands:')
  console.log('  window.testPanRight()      - Pan right 500px')
  console.log('  window.testPanLeft()       - Pan left 300px')
  console.log('  window.testJumpToFrame(n)  - Jump to frame n')
  console.log('  window.testViewportInfo()  - Show viewport state')
  console.log('  window.testSmoothPan()     - Smooth pan simulation')
  console.log('  window.testReset()         - Reset viewport to origin')
  console.log('  window.testShowAll()       - Center to see all frames')
  console.log('')
  console.log('  Phase 2.3 Gesture Tests:')
  console.log('  window.testSwipeToWorldDirection()  - Test swipe→direction conversion')
  console.log('  window.testFindCamerasInDirection() - Test cone-based camera search')
  console.log('  window.testScoreCandidates()        - Test dual-metric scoring')
  console.log('  window.testAllGestureConversion()   - Run all Phase 2.3 tests')
  console.log('')
  console.log('  Debug Helpers:')
  console.log('  window.getStoreForDebug()  - Get current app state')
}
